import React, { useState } from "react";
import {
  Stack,
  Button,
  Table,
  Badge,
  Group,
  ActionIcon,
  Drawer,
  Text,
} from "@mantine/core";
import { IconPencil, IconTrash, IconPlus } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import McaStepNotificationForm, {
  notificationSectionForError,
} from "./McaStepNotificationForm";
import {
  defaultStepNotification,
  summarizeNotificationRecipients,
  summarizeNotificationWhen,
  validateStepNotification,
  MCA_NOTIFY_CHANNELS,
} from "../../data/mcaEntities";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";

const channelLabel = (channel) =>
  MCA_NOTIFY_CHANNELS.find((c) => c.value === channel)?.label ?? channel;

const McaStepNotificationsList = ({ notifications = [], onChange, targetEntity }) => {
  const readOnly = useMcaEditorReadOnly();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [draft, setDraft] = useState(defaultStepNotification());
  const [section, setSection] = useState("setup");

  const openAdd = () => {
    setEditIndex(-1);
    setDraft(defaultStepNotification());
    setSection("setup");
    setDrawerOpen(true);
  };

  const openEdit = (index) => {
    setEditIndex(index);
    setDraft({ ...defaultStepNotification(), ...notifications[index] });
    setSection("setup");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditIndex(-1);
    setDraft(defaultStepNotification());
  };

  const handleSave = () => {
    const error = validateStepNotification(draft);
    if (error) {
      setSection(notificationSectionForError(error));
      showNotification({ title: "Validation", message: error, color: "red" });
      return;
    }
    const next = [...notifications];
    const item = {
      ...draft,
      name: draft.name.trim(),
      subject: draft.subject?.trim() ?? "",
      message: draft.message?.trim() ?? "",
    };
    if (editIndex >= 0) {
      next[editIndex] = item;
    } else {
      next.push(item);
    }
    onChange(next);
    closeDrawer();
  };

  const handleDelete = (index) => {
    onChange(notifications.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing="sm">
      <Text size="xs" color="dimmed">
        Add one or more notification rules for this step. Each rule can use a
        different channel, trigger, and recipients.
      </Text>
      {notifications.length > 0 ? (
        <Table fontSize="xs" striped highlightOnHover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Channel</th>
              <th>When</th>
              <th>Recipients</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {notifications.map((row, idx) => (
              <tr key={idx}>
                <td>{row.name}</td>
                <td>
                  <Badge size="xs" variant="outline">
                    {channelLabel(row.channel ?? "EMAIL")}
                  </Badge>
                </td>
                <td>{summarizeNotificationWhen(row)}</td>
                <td>{summarizeNotificationRecipients(row)}</td>
                <td>
                  <Group spacing={4} noWrap>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      aria-label="View notification"
                      onClick={() => openEdit(idx)}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                    {!readOnly && (
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => handleDelete(idx)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                    )}
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Text size="xs" color="dimmed">
          No notifications configured.
        </Text>
      )}
      {!readOnly && (
      <Button
        size="xs"
        w="fit-content"
        variant="light"
        leftIcon={<IconPlus size={14} />}
        onClick={openAdd}
      >
        Add notification
      </Button>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        title={
          readOnly
            ? "Notification"
            : editIndex >= 0
              ? "Edit notification"
              : "Add notification"
        }
        position="right"
        size="50%"
        padding="xl"
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 60px)",
          },
        }}
      >
        <Stack spacing={0} sx={{ flex: 1, minHeight: 0 }}>
          <McaStepNotificationForm
            value={draft}
            onChange={setDraft}
            targetEntity={targetEntity}
            section={section}
            onSectionChange={setSection}
          />
        </Stack>
        <Group position="right" pt="md" mt="auto">
          <Button variant="default" onClick={closeDrawer}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && <Button onClick={handleSave}>Save</Button>}
        </Group>
      </Drawer>
    </Stack>
  );
};

export default McaStepNotificationsList;
